import { type ReactNode, createContext, useContext, useState } from "react";
import { DataStore } from "../store";

/**
 * Data context.
 */
const DataContext = createContext<DataStore | null>(null);

/**
 * Hook to access the data store.
 *
 * @returns DataStore instance
 * @throws Error if used outside DataProvider
 */
export function useDataStore(): DataStore {
	const store = useContext(DataContext);
	if (!store) {
		throw new Error("useDataStore must be used within DataProvider");
	}
	return store;
}

/**
 * Hook to access the data store (optional).
 * Returns null if not within provider instead of throwing.
 *
 * @returns DataStore instance or null
 */
export function useDataStoreOptional(): DataStore | null {
	return useContext(DataContext);
}

/**
 * Props for DataProvider.
 */
export interface DataProviderProps {
	children: ReactNode;
	/** Optional initial store (for testing) */
	store?: DataStore;
}

/**
 * Provider for data context.
 * Creates a DataStore instance that persists for the lifetime of the provider.
 */
export function DataProvider({ children, store: initialStore }: DataProviderProps) {
	const [store] = useState(() => initialStore ?? new DataStore());

	return <DataContext.Provider value={store}>{children}</DataContext.Provider>;
}
